import { FC, useCallback, useEffect, useRef, useState } from 'react';
import './styles/index.css';
import useMediaQuery from '@/hooks/useMediaQuery';
import { mediumDesktop, desktop, tablet } from '@/constants/screensizes';
import Image from '@/components/atoms/Image';
import Slider from '@/components/atoms/slider';
import Swiper from 'swiper';
import debounce from 'lodash.debounce';

declare global {
  interface Window {
    ConfigureUI?: any;
  }
}
interface GalleryProps {
  images: Array<string>;
  sku?: string;
  color?: string;
}

const Gallery: FC<GalleryProps> = ({ images, sku, color }) => {
  const [isTablet] = useMediaQuery(tablet);
  const [isDesktop] = useMediaQuery(desktop);
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [isConfigured, setIsConfigured] = useState(false);
  const isSingleImage = images.length === 1;
  const imagesToDisplay = images;
  const [transitionDuration, setTransitionDuration] = useState(1000);
  const swiperRef = useRef<Swiper>(null);
  const setLoading = (loading: any) => {};
  const clearLoading = useCallback(
    debounce(() => setLoading(false), 1000),
    [setLoading],
  );


  useEffect(() => {
    clearLoading();
  }, [clearLoading]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://inhaabitcdn.com/configure-ui/v4/ConfigureUI.min.js';
    script.async = true;
    script.onload = () => {
      console.log('ConfigureUI script loaded');
      setIsConfigured(true);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const clearImage = useCallback(
    debounce(() => setLoading(false), 750),
    [setLoading],
  );
  useEffect(() => {
    clearImage();
  }, []);



  const reinitializeConfigureUI = useCallback(() => {
    if (isConfigured && swiperRef.current) {
      const applyToAll = images.length === 1;
      swiperRef?.current?.slides?.forEach((slide, index) => {
        if (applyToAll || index % 2 === 0) {
          new window.ConfigureUI({
            id: '64180e9626eee11f02dac5b7',
            mode: 'lightbox',
            buttonPosition: 'inline',
            sku: color ? `${sku}*${color}` : sku,
            element: slide,
          });
        }
      });
    }
  }, [isConfigured, images, sku, color]);

  useEffect(() => {
    reinitializeConfigureUI();
  }, [reinitializeConfigureUI, sku, color, images.length]);

  return (
    <div className="gallery w-full px-0">
      {images.length === 0 ? (
        <div>
          <Image
            priority
            src="/images/No_product_Image.jpg"
            alt={'emptyImage'}
            sizes="100vw"
            style={{
              width: '100%',
              height: '383px',
              maxWidth: '500px',
              objectFit: 'cover',
              paddingLeft: isDesktop ? '20px' : '0px',
            }}
            width={500}
            height={300}
            onLoad={() => setLoading(false)}
          />
        </div>
      ) : (
        <Slider
          arrows={isDesktop && !isSingleImage}
          dots={!isDesktop && !isSingleImage}
          loop={!isSingleImage}
          speed={transitionDuration}
          prevButtonStyles={{
            left:isMediumDesktop? 0: isDesktop ? "18px" : -4,
            backgroundColor: 'white',
            padding: isDesktop ? '29px' : '16px',
            zIndex: 10,
          }}
          nextButtonStyles={{
            right: isDesktop ? 0 : -4,
            backgroundColor: 'white',
            padding: isDesktop ? '29px' : '16px',
            zIndex: 10,
          }}
          slidesPerView={isTablet ? 2 : 1}
          spaceBetween={8}
          allowArrowsOnTouchDevice={!isSingleImage}
          allowTouchMove={!isSingleImage}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {imagesToDisplay?.map((image, index) => (
            <div key={index} className={`relative`}>
              <div className="my-360-container">
                <Image
                  priority
                  src={image}
                  alt={image}
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '680px',
                    objectFit: 'contain',
                    paddingLeft: isDesktop ? '20px' : '0px',
                  }}
                  width={500}
                  height={500}
                  onLoad={() => setLoading(false)}
                />
              </div>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default Gallery;
